import React, { useCallback, useEffect, useState } from "react";
import {
  CardDetail,
  CSSSteps,
  JobScheduleDetails,
  ManageApplicationCardControlled,
  SFAppStep,
} from "./card-controlled";
import { SdaDetails } from "../appointment-card/card-controlled";
import * as adobeAnalytics from "../../utility/adobe-analytics";
import { useParams } from "react-router-dom";

interface ManageApplicationCardUncontrolledProps {
  jcAccessor: () => Promise<JobScheduleDetails | undefined>;
  sdaAccessor: () => Promise<SdaDetails | undefined>;
  sfAccessor: () => Promise<SFAppStep | undefined>;
  cssURLWithScheduleId: string;
  cssURLWithoutScheduleId: string;
}

export const ManageApplicationCardUncontrolled = ({
  jcAccessor,
  sdaAccessor,
  sfAccessor,
  cssURLWithScheduleId,
  cssURLWithoutScheduleId,
}: ManageApplicationCardUncontrolledProps) => {
  const { requisitionId, applicationId } = useParams<{
    requisitionId: string;
    applicationId: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [scheduleDetails, setScheduleDetails] = useState<
    JobScheduleDetails | undefined
  >();
  const [eligibleForCSS, setEligibleForCSS] = useState<boolean>(false);
  const [startDateDetails, setStartDateDetails] = useState<
    SdaDetails | undefined
  >();
  const [scheduleIdFound, setScheduleIdFound] = useState<boolean>(true);

  const sendAnalyticsEvent = useCallback(
    async (eventName: string) => {
      const scheduleDetails = await jcAccessor();
      await adobeAnalytics.addEventMetric(window, applicationId, eventName, {
        dragonstoneSchedule: {
          scheduleID: scheduleDetails?.scheduleId,
          jobID: requisitionId,
        },
        geocluster: {
          ID: scheduleDetails?.geoClusterId,
        },
      });
    },
    [applicationId]
  );

  const trackStartDateDetailsClick = useCallback(
    () => sendAnalyticsEvent("first day change link click"),
    [sendAnalyticsEvent]
  );

  const requestJCAndSDA = useCallback(async () => {
    setLoading(true);
    try {
      const scheduleDetails = await jcAccessor();
      const startDateDetails = await sdaAccessor();
      const sfAppStep = await sfAccessor();

      if (scheduleDetails?.error) {
        if (
          scheduleDetails?.error?.errorCode === "APPLICATION_DETAILS_NOT_FOUND"
        ) {
          setScheduleIdFound(false);
        }
        setError(true);
      } else {
        setScheduleDetails(scheduleDetails);
        setError(false);
      }

      if (startDateDetails?.errorCode) {
        setError(true);
      } else {
        setStartDateDetails(startDateDetails);
        setError(false);
      }

      if (
        CSSSteps.includes(
          sfAppStep?.sfApplicationStepSubStep
            ? sfAppStep.sfApplicationStepSubStep
            : ""
        )
      ) {
        setEligibleForCSS(true);
      }
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    requestJCAndSDA();
  }, []);

  let totalPay = scheduleDetails?.basePay;
  if (totalPay && scheduleDetails?.shiftDifferential) {
    totalPay += scheduleDetails?.shiftDifferential;
  }

  const details: CardDetail = {
    jobTitle: scheduleDetails?.externalJobTitle as string,
    hours: scheduleDetails?.scheduleText as string,
    startDate: startDateDetails?.startDate as string,
    startTime: startDateDetails?.startTime as string,
    description: scheduleDetails?.briefJobDescription as string,
    payRate: totalPay?.toFixed(2) as string,
    address: startDateDetails?.address as string,
    image: scheduleDetails?.image as string,
    startDateStatus: startDateDetails?.startDateStatus as string,
  };

  return (
    <ManageApplicationCardControlled
      details={details}
      onRetry={requestJCAndSDA}
      loading={loading}
      error={error}
      eligibleForCSS={eligibleForCSS}
      scheduleIdFound={scheduleIdFound}
      cssURLWithScheduleId={cssURLWithScheduleId}
      cssURLWithoutScheduleId={cssURLWithoutScheduleId}
      onChangeSchedule={trackStartDateDetailsClick}
    />
  );
};
